/**
 * GL-DESIGN-V3-2026Q4 — Dashboard (Instrument style).
 *
 * Keeps the existing data flow (list engagements, filter, delete) and
 * replaces the layout with v3 primitives: navy "Next up" card, tint
 * attention card, raised KPI tiles, raised engagement rows with mini
 * score dial + status pill.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { TEST_IDS } from '@/constants/ids';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  Card,
  PrimaryPill,
  OutlinePill,
  IconButton,
  Segmented,
  StatusPill,
  ScoreDial,
  MiniScoreDial,
  Eyebrow,
  V3,
} from '@/components/v3/kit';
import { PlayIcon, ArrowRightIcon, ArrowUpRightIcon, PhoneIcon, FirstAidIcon, ClipboardTextIcon, BuildingsIcon, CalendarBlankIcon, TrashIcon } from '@phosphor-icons/react';

const STATUS_META = {
  draft: { dot: V3.muted, label: 'Draft' },
  in_progress: { dot: V3.navy, label: 'In progress' },
  pending_review: { dot: V3.gold, label: 'Pending review' },
  report_ready: { dot: V3.gold, label: 'Report ready' },
  delivered: { dot: V3.p3, label: 'Delivered' },
  archived: { dot: V3.muted, label: 'Archived' },
};

function greetingByTime() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function fmtDateFull() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export default function DashboardPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [archived, setArchived] = useState(null);
  const [tab, setTab] = useState('active'); // 'active' | 'completed' | 'archived'
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const [active, arch] = await Promise.all([
        api.get('/engagements'),
        api.get('/engagements?archived=true'),
      ]);
      setItems(active.data);
      setArchived(arch.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
      setArchived([]);
    }
  }

  useEffect(() => { load(); }, []);

  async function deleteEngagement(eng) {
    if (!window.confirm(`Permanently delete ${eng.business_name}?\n\nThis removes the engagement and ALL findings, photos, docs, follow-ups, and reports.\n\nThis cannot be undone.`)) return;
    setBusyId(eng.id);
    try {
      await api.delete(`/engagements/${eng.id}`);
      toast.success(`${eng.business_name} deleted.`);
      setItems((prev) => (prev || []).filter((x) => x.id !== eng.id));
      setArchived((prev) => (prev || []).filter((x) => x.id !== eng.id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  // ---- derived data ----
  const activeList = items || [];
  const archList = archived || [];
  const loading = items === null || archived === null;

  const visible = useMemo(() => {
    if (tab === 'archived') return archList;
    if (tab === 'completed') return activeList.filter((x) => x.status === 'delivered');
    return activeList.filter((x) => x.status !== 'delivered');
  }, [tab, activeList, archList]);

  // Next up: top active engagement by created_at desc
  const nextUp = useMemo(() => {
    const active = activeList.filter((x) => ['in_progress', 'draft', 'pending_review'].includes(x.status));
    return active[0] || null;
  }, [activeList]);

  // Attention: a delivered report that may be unopened. We don't have open-state here
  // directly, so pick the most recent delivered engagement that has no open yet (null
  // ok — we show a soft nudge card either way as it's informational).
  const attention = useMemo(() => {
    const delivered = activeList.filter((x) => x.status === 'delivered');
    return delivered[0] || null;
  }, [activeList]);

  // KPI tiles — simple roll-ups
  const kpis = useMemo(() => {
    const total = activeList.length;
    const inProgress = activeList.filter((x) => x.status === 'in_progress').length;
    const delivered = activeList.filter((x) => x.status === 'delivered').length;
    const pendingReview = activeList.filter((x) => x.status === 'pending_review' || x.status === 'report_ready').length;
    return [
      { icon: BuildingsIcon, label: 'Engagements', value: total, trend: null, trendColor: V3.muted },
      { icon: ClipboardTextIcon, label: 'In progress', value: inProgress, trend: null, trendColor: V3.muted },
      { icon: FirstAidIcon, label: 'Pending review', value: pendingReview, trend: null, trendColor: V3.muted },
      { icon: CalendarBlankIcon, label: 'Delivered', value: delivered, trend: null, trendColor: V3.muted },
    ];
  }, [activeList]);

  return (
    <div style={{ background: V3.surface, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '28px 20px 48px' }}>
        {/* ============ Header row ============ */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: V3.muted }}>{fmtDateFull()}</div>
            <h1 style={{ fontSize: 'clamp(26px, 3vw, 32px)', fontWeight: 700, color: V3.navy, letterSpacing: '-0.025em', marginTop: 2 }}>
              {greetingByTime()}, {auth?.user?.name?.split(' ')[0] || 'Vince'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <IconButton icon="magnifying-glass" size={52} testid="dashboard-search-btn" title="Search" />
            <PrimaryPill
              icon="plus"
              onClick={() => nav('/engagements/new')}
              testid={TEST_IDS?.dashNewEngagement || 'dash-new-engagement'}
            >
              New engagement
            </PrimaryPill>
          </div>
        </div>

        {/* ============ Hero row — Next up + Attention ============ */}
        {!loading && (nextUp || attention) && (
          <div
            className="grid gap-4 mb-6"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
          >
            {/* Next-up navy card */}
            {nextUp && (
              <div
                onClick={() => nav(`/engagements/${nextUp.id}`)}
                data-testid="dash-next-up"
                style={{
                  background: V3.navy,
                  borderRadius: 26,
                  padding: 22,
                  color: '#fff',
                  cursor: 'pointer',
                  boxShadow: V3.navyCard,
                  minHeight: 190,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <Eyebrow color={V3.gold}>NEXT UP · {nextUp.visit_date || 'Today'}</Eyebrow>
                <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.15, marginTop: 4 }}>{nextUp.business_name}</div>
                <div style={{ fontSize: 13, color: '#c4cbe0', lineHeight: 1.5 }}>
                  {[nextUp.site_address, nextUp.industry].filter(Boolean).join(' · ') || 'Walkthrough scheduled'}
                </div>
                <div className="mt-auto flex items-center justify-between pt-4">
                  <span style={{ fontSize: 15, fontWeight: 600 }}>Start walkthrough</span>
                  <span
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      background: '#fff',
                      color: V3.navy,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayIcon size={22} weight="fill" />
                  </span>
                </div>
              </div>
            )}

            {/* Attention tint card */}
            {attention && (
              <div
                onClick={() => nav(`/engagements/${attention.id}?tab=report`)}
                data-testid="dash-attention"
                style={{
                  background: V3.tint,
                  borderRadius: 26,
                  padding: 22,
                  minHeight: 190,
                  position: 'relative',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 18,
                    right: 18,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: `1.5px solid ${V3.navy}`,
                    color: V3.navy,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ArrowUpRightIcon size={18} weight="bold" />
                </div>
                <StatusPill dot={V3.p2} bg="#fff">
                  Delivered · check-in due
                </StatusPill>
                <div style={{ fontSize: 20, fontWeight: 700, color: V3.navy, marginTop: 4 }}>{attention.business_name}</div>
                <div style={{ fontSize: 13.5, color: V3.muted, lineHeight: 1.5 }}>
                  Report delivered — tap to review open-rate and follow-up status.
                </div>
                <div className="mt-auto pt-3">
                  <OutlinePill icon="phone">Open engagement</OutlinePill>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============ KPI tiles ============ */}
        {!loading && (
          <div
            className="grid gap-4 mb-6"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}
          >
            {kpis.map((k) => (
              <Card key={k.label} padding={18} radius={22} testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: V3.tint,
                      color: V3.navy,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <k.icon size={18} weight="bold" />
                  </span>
                  {k.trend && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: k.trendColor }}>{k.trend}</span>
                  )}
                </div>
                <div style={{ fontSize: 30, fontWeight: 700, color: V3.navy, marginTop: 10, letterSpacing: '-0.02em' }}>
                  {k.value}
                </div>
                <div style={{ fontSize: 12.5, color: V3.muted, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginTop: 2 }}>
                  {k.label}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ============ Engagements section ============ */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 style={{ fontSize: 18, fontWeight: 700, color: V3.navy }}>Engagements</h2>
          <Segmented
            testid="dash-filter"
            value={tab}
            onChange={setTab}
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Delivered', value: 'completed' },
              { label: 'Archived', value: 'archived' },
            ]}
          />
        </div>

        {loading && (
          <Card padding={28}>
            <div style={{ color: V3.muted, fontSize: 14 }}>Loading engagements…</div>
          </Card>
        )}

        {!loading && visible.length === 0 && (
          <Card padding={32}>
            <div style={{ fontSize: 16, fontWeight: 700, color: V3.navy }}>No engagements yet</div>
            <div style={{ fontSize: 14, color: V3.muted, marginTop: 6, maxWidth: 480 }}>
              Tap "New engagement" above to start your first walkthrough.
            </div>
          </Card>
        )}

        {!loading && visible.length > 0 && (
          <div className="grid gap-3" style={{ gridTemplateColumns: '1fr' }}>
            {visible.map((eng) => {
              const meta = STATUS_META[eng.status] || STATUS_META.draft;
              const pct = Math.round(eng.compliance_score || 0);
              return (
                <div
                  key={eng.id}
                  data-testid={`eng-row-${eng.id}`}
                  onClick={() => nav(`/engagements/${eng.id}`)}
                  style={{
                    background: V3.surface,
                    borderRadius: 22,
                    boxShadow: V3.raised,
                    padding: 14,
                    display: 'grid',
                    gridTemplateColumns: '60px minmax(0, 1fr) auto 44px',
                    gap: 14,
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <MiniScoreDial pct={pct} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15.5, fontWeight: 700, color: V3.navy, letterSpacing: '-0.01em' }}>
                        {eng.business_name}
                      </span>
                      <StatusPill dot={meta.dot}>{meta.label}</StatusPill>
                    </div>
                    <div style={{ fontSize: 12.5, color: V3.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[eng.site_address, eng.industry, eng.visit_date].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteEngagement(eng);
                    }}
                    disabled={busyId === eng.id}
                    aria-label="Delete engagement"
                    data-testid={`eng-delete-${eng.id}`}
                    style={{
                      background: 'transparent',
                      border: 0,
                      color: V3.muted,
                      cursor: busyId === eng.id ? 'wait' : 'pointer',
                      opacity: busyId === eng.id ? 0.5 : 0.6,
                      padding: 8,
                    }}
                  >
                    <TrashIcon size={16} weight="bold" />
                  </button>
                  <span
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: V3.tint,
                      color: V3.navy,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArrowRightIcon size={18} weight="bold" />
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
